import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { BookMarked, Check, FlaskConical, Search } from 'lucide-react'
import { useState } from 'react'
import type { BrowseRecord } from '../database/RecordCard'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { EmptyState, LoadingPanel } from '@/components/ui/misc'
import { useDebounced } from '@/hooks/useDebounced'
import { api, queryString } from '@/lib/api'
import { cn, emptyDash } from '@/lib/utils'

const PAGE_SIZE = 20

/**
 * Adds records to a comparison. Selection is staged locally and only handed
 * back on confirm, so browsing around does not keep rewriting the comparison
 * behind the researcher.
 */
export function RecordPicker({
  open,
  onOpenChange,
  alreadyChosen,
  remainingSlots,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  alreadyChosen: string[]
  remainingSlots: number
  onConfirm: (ids: string[]) => void
}) {
  const [search, setSearch] = useState('')
  const [staged, setStaged] = useState<string[]>([])
  const debounced = useDebounced(search, 250)

  const results = useQuery({
    queryKey: ['compare', 'picker', debounced],
    queryFn: () =>
      api.get<{ records: BrowseRecord[]; total: number }>(
        `/browse${queryString({ q: debounced, sort: 'recent', page: 1, pageSize: PAGE_SIZE })}`,
      ),
    enabled: open,
    placeholderData: keepPreviousData,
  })

  const close = () => {
    setStaged([])
    setSearch('')
    onOpenChange(false)
  }

  const toggle = (id: string) =>
    setStaged((current) =>
      current.includes(id)
        ? current.filter((entry) => entry !== id)
        : current.length >= remainingSlots
          ? current
          : [...current, id],
    )

  const records = results.data?.records ?? []

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Add records to comparison"
      description={
        remainingSlots > 0
          ? `You can add ${remainingSlots} more record${remainingSlots === 1 ? '' : 's'}.`
          : 'A comparison holds at most five records. Remove one to add another.'
      }
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            disabled={staged.length === 0}
            onClick={() => {
              onConfirm(staged)
              close()
            }}
          >
            Add {staged.length || ''} to comparison
          </Button>
        </>
      }
    >
      <div className="pb-4">
        <Input
          value={search}
          autoFocus
          icon={<Search className="size-4" />}
          placeholder="Search by title, code or dye source…"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {results.isLoading ? (
        <LoadingPanel label="Searching records" />
      ) : records.length === 0 ? (
        <EmptyState
          title="No records found"
          description={debounced ? `Nothing matches "${debounced}".` : 'The database is empty.'}
        />
      ) : (
        <ul className="space-y-1.5">
          {records.map((record) => {
            const chosen = alreadyChosen.includes(record.id)
            const selected = staged.includes(record.id)
            const full = !selected && staged.length >= remainingSlots

            return (
              <li key={record.id}>
                <button
                  type="button"
                  disabled={chosen || full}
                  onClick={() => toggle(record.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-[var(--radius-control)] border px-3 py-2 text-left transition-colors',
                    selected
                      ? 'border-brand-line bg-brand-soft'
                      : 'border-line hover:bg-paper-muted',
                    (chosen || full) && 'cursor-not-allowed opacity-50 hover:bg-transparent',
                  )}
                >
                  <span
                    className="size-10 shrink-0 rounded-[6px] border border-line"
                    style={{ backgroundColor: record.swatchHex ?? 'var(--color-paper-muted)' }}
                  />

                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
                      {record.kind === 'reference' ? (
                        <BookMarked className="size-3" />
                      ) : (
                        <FlaskConical className="size-3" />
                      )}
                      {record.code}
                    </span>
                    <span className="block truncate text-sm text-ink">{record.title}</span>
                    <span className="block truncate text-[12px] text-ink-muted">
                      {emptyDash(record.dyeSourceName)} · {emptyDash(record.fiberName)} ·{' '}
                      {emptyDash(record.researcherName)}
                    </span>
                  </span>

                  {chosen ? (
                    <span className="shrink-0 text-[12px] text-ink-muted">In comparison</span>
                  ) : (
                    selected && <Check className="size-4 shrink-0 text-brand" />
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </Dialog>
  )
}
